import { Navigate, useLocation } from 'react-router-dom';
import { ShieldX, Lock, Loader2 } from 'lucide-react';

import { useAuth } from '../context/AuthContext';

function ProtectedRoute({ children, requiredPermission, requiredRole, requiredFeature, fallbackPath = '/login' }) {
  const { user, loading, hasFeature, hasPermission } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-secondary-50">
        <div className="text-center">
          <Loader2 size={40} className="animate-spin text-primary-500 mx-auto mb-5" />
          <p className="text-secondary-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  const allowedRoles = Array.isArray(requiredRole) ? requiredRole : (requiredRole ? [requiredRole] : []);
  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return (
      <div className="flex items-center justify-center h-screen bg-secondary-50 px-4">
        <div className="text-center p-10 bg-white rounded-xl shadow-sm border border-secondary-200 max-w-sm w-full">
          <ShieldX size={48} className="text-red-500 mx-auto mb-5" />
          <h2 className="text-xl font-bold text-secondary-900 mb-2.5">
            Access Denied
          </h2>
          <p className="text-secondary-500 mb-5">
            You don't have the required role to access this page.
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-5 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (requiredFeature && !hasFeature(requiredFeature)) {
    return (
      <div className="flex items-center justify-center h-screen bg-secondary-50 px-4">
        <div className="text-center p-10 bg-white rounded-xl shadow-sm border border-secondary-200 max-w-sm w-full">
          <Lock size={48} className="text-primary-500 mx-auto mb-5" />
          <h2 className="text-xl font-bold text-secondary-900 mb-2.5">
            Pro Feature
          </h2>
          <p className="text-secondary-500 mb-5">
            Your current plan does not include this feature. Upgrade to Pro to continue.
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-5 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <div className="flex items-center justify-center h-screen bg-secondary-50 px-4">
        <div className="text-center p-10 bg-white rounded-xl shadow-sm border border-secondary-200 max-w-sm w-full">
          <Lock size={48} className="text-secondary-400 mx-auto mb-5" />
          <h2 className="text-xl font-bold text-secondary-900 mb-2.5">
            Permission Denied
          </h2>
          <p className="text-secondary-500 mb-5">
            You don't have permission to access this resource.
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-5 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return children;
}

export default ProtectedRoute;
